
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Appointment } from '../types/appointmentTypes';
import { useBookingRealtimeSync } from '@/hooks/useBookingRealtimeSync';

export const useAppointmentHistory = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [appointmentToDelete, setAppointmentToDelete] = useState<string | null>(null);
  const [selectedAppointmentForRefund, setSelectedAppointmentForRefund] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  const fetchAppointments = useCallback(async () => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      console.log('Fetching appointment history...');
      
      const now = new Date().toISOString();
      
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          id, 
          start_time, 
          end_time, 
          status, 
          format, 
          price_at_booking, 
          currency_at_booking,
          client_notes,
          location_details,
          refund_status,
          refund_reason,
          payment_status,
          service:services!inner (service_name),
          practitioner:practitioner_id (first_name, last_name, avatar_url),
          practitioner_id
        `)
        .eq('client_id', user.id)
        .or(`status.neq.Scheduled,start_time.lt.${now}`) // Non-scheduled OR past appointments
        .order('start_time', { ascending: false });
        
      if (error) throw error;
      
      if (data) {
        console.log(`Fetched ${data.length} appointments from database:`, data);
        const formattedData: Appointment[] = data.map(item => ({
          id: item.id,
          start_time: item.start_time,
          end_time: item.end_time,
          status: item.status,
          format: item.format,
          price_at_booking: item.price_at_booking,
          currency_at_booking: item.currency_at_booking,
          client_notes: item.client_notes,
          location_details: item.location_details,
          refund_status: item.refund_status,
          refund_reason: item.refund_reason,
          payment_status: item.payment_status || 'Unknown',
          service: {
            service_name: item.service?.service_name || 'Unknown Service'
          },
          practitioner: {
            first_name: item.practitioner?.first_name || null,
            last_name: item.practitioner?.last_name || null,
            avatar_url: item.practitioner?.avatar_url || null
          },
          practitioner_id: item.practitioner_id
        }));
        
        setAppointments(formattedData);
        console.log('Formatted appointments:', formattedData);
      } else {
        setAppointments([]);
      }
    } catch (error) {
      console.error('Error fetching appointment history:', error);
      toast({
        title: "Failed to load history",
        description: "There was an error loading your appointment history",
        variant: "destructive",
        duration: 8000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [user, toast]);

  const handleDeleteAppointment = async (appointmentId: string) => {
    if (!user) return;
    
    try {
      setIsDeleting(true);
      console.log('Deleting appointment:', appointmentId);
      
      const { error } = await supabase
        .from('bookings')
        .delete()
        .eq('id', appointmentId)
        .eq('client_id', user.id);
        
      if (error) throw error;
      
      // Immediately remove from state
      setAppointments(prev => prev.filter(app => app.id !== appointmentId));
      
      toast({
        title: "Appointment deleted",
        description: "The appointment has been removed from your history",
      });
      
    } catch (error) {
      console.error('Error deleting appointment:', error);
      toast({
        title: "Failed to delete",
        description: "There was an error deleting the appointment",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setAppointmentToDelete(null);
    }
  };

  // Refresh data when the component mounts, when user changes, or when refreshTrigger changes
  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments, refreshTrigger]);

  // Function to manually trigger a refresh
  const refreshAppointments = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Add realtime sync for appointment updates
  useBookingRealtimeSync({
    userId: user?.id,
    type: 'client',
    onBookingUpdate: (updatedBooking) => {
      console.log('Booking update received in history:', updatedBooking);
      setAppointments(appointments => {
        const existingAppointmentIndex = appointments.findIndex(appt => appt.id === updatedBooking.id);
        
        if (existingAppointmentIndex >= 0) {
          const updatedAppointments = [...appointments];
          updatedAppointments[existingAppointmentIndex] = {
            ...updatedAppointments[existingAppointmentIndex],
            ...updatedBooking,
            payment_status: updatedBooking.payment_status || updatedAppointments[existingAppointmentIndex].payment_status
          };
          return updatedAppointments;
        } else {
          return appointments;
        }
      });
    },
    onBookingInsert: (newBooking) => {
      console.log('New booking received in history:', newBooking);
      fetchAppointments();
    },
    onBookingDelete: (deletedBooking) => {
      console.log('Booking deletion received in history:', deletedBooking);
      setAppointments(appointments => appointments.filter(appt => appt.id !== deletedBooking.id));
    }
  });

  return {
    appointments,
    isLoading,
    isDeleting,
    appointmentToDelete,
    selectedAppointmentForRefund,
    setAppointmentToDelete,
    setSelectedAppointmentForRefund,
    fetchAppointments,
    refreshAppointments,
    handleDeleteAppointment
  };
};
